import { useState } from "react";
import classNames from "classnames";
import { Formik, setNestedObjectValues, useFormikContext } from "formik";
import { get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  BiCheck,
  BiChevronLeft,
  BiCopy,
  BiLock,
  BiTrash,
} from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import { GENDER, ORGANIZATION_FORMS } from "utils/constants";
import { fill } from "utils/helpers/template";
import { Logo, SelectField, TextField } from "../Create/Fields";
import IntegrationsStep from "../Create/IntegrationsStep";
import { laterFields, STEP_KEYS } from "../Create/onboarding";
import RemainingPanel from "../Create/RemainingPanel";
import validation from "../Create/validation";
import ApiAccess from "./ApiAccess";
import EncryptionKey from "./EncryptionKey";
import Invitation from "./Invitation";
import Segmented from "./Segmented";
import SetupList from "./SetupList";
import {
  ATTENTION_TONES,
  ENCRYPTION_STATUSES,
  INVITATION_STATUSES,
  keyStatusOf,
  laterFieldLabelsOf,
  SECTIONS,
  SETUP_SECTIONS,
  setupItemsOf,
  setupStatusOf,
  setupTodosOf,
  STATUS_TONES,
  todoStatusOf,
} from "./setup";

const GENDERS = [GENDER.MALE, GENDER.FEMALE];

const Civility = ({ labels }) => {
  const { values, errors, touched, setFieldValue } = useFormikContext();
  const value = get(values, "user.gender");
  const error = get(touched, "user.gender") && get(errors, "user.gender");
  return (
    <fieldset className="onboarding__field onboarding__fieldset">
      <legend>
        {labels.GENDER}
        <span className="onboarding__required"> *</span>
      </legend>
      <Segmented
        value={value}
        options={GENDERS.map((gender) => ({
          value: gender,
          label: get(labels.GENDERS, gender, gender),
        }))}
        onChange={(gender) => setFieldValue("user.gender", gender)}
      />
      {error ? <span className="onboarding__error">{error}</span> : null}
    </fieldset>
  );
};

Civility.propTypes = {
  labels: PropTypes.object.isRequired,
};

const Page = ({
  catalog,
  setup,
  invitation,
  encryption,
  encryptionBusy,
  api,
  saving,
  emailReadOnly,
  onCopyLink,
  onResend,
  onLinkKey,
  onPrepareKeyImport,
  onImportKey,
  onVerifyKey,
  onSendKeyHandover,
  onCancel,
  onDelete,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const ONBOARDING = t("ONBOARDING", { returnObjects: true });
  const GENERAL = ONBOARDING.GENERAL;
  const ENCRYPTION = DETAIL.ENCRYPTION;
  const SECTION_TEXT = { ...DETAIL.SECTIONS, KEY: ENCRYPTION.SECTION };
  const { t: brokerT } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const formOptions = brokerT("COMPANY_FORM_OPTIONS", { returnObjects: true });
  const { values, initialValues, validateForm, setTouched, submitForm } =
    useFormikContext();
  const [section, setSection] = useState(STEP_KEYS.GENERAL);

  const type = get(initialValues, "organization.type");
  const adminName = [
    get(initialValues, "user.firstname"),
    get(initialValues, "user.lastname"),
  ]
    .filter(Boolean)
    .join(" ");
  const pending = invitation.status === INVITATION_STATUSES.PENDING;
  const keyStatus = keyStatusOf(encryption);
  const keysLocked = keyStatus !== ENCRYPTION_STATUSES.ACTIVE;
  const setupContext = {
    keyStatus,
    access: api.access,
    labels: DETAIL,
    t,
  };

  const todo = {
    [STEP_KEYS.GENERAL]: laterFieldLabelsOf(laterFields(values), GENERAL),
    [STEP_KEYS.ADMIN]: pending
      ? [fill(DETAIL.REMAINING.ADMIN, { name: adminName })]
      : [],
    ...setupTodosOf({ ...setupContext, values, catalog, setup, type }),
  };

  const statusOf = (key) => {
    if (key === STEP_KEYS.ADMIN && pending)
      return { tone: STATUS_TONES.TODO, text: DETAIL.STATUS.PENDING };
    if (
      key === STEP_KEYS.ADMIN &&
      invitation.status === INVITATION_STATUSES.ACCEPTED
    )
      return { tone: STATUS_TONES.DONE, text: DETAIL.STATUS.ACCEPTED };
    const setupStatus = setupStatusOf(key, setupContext);
    if (setupStatus) return setupStatus;
    if (!isEmpty(todo[key])) return todoStatusOf(key, todo[key], t);
    return { tone: STATUS_TONES.DONE, text: DETAIL.STATUS.COMPLETE };
  };

  const save = async () => {
    const errors = await validateForm();
    if (isEmpty(errors)) return submitForm();
    setTouched(setNestedObjectValues(errors, true));
    return setSection(
      isEmpty(errors.organization) ? STEP_KEYS.ADMIN : STEP_KEYS.GENERAL,
    );
  };

  const copyButton = (
    <button
      type="button"
      className="onboarding__button onboarding__button--outline onboarding__button--icon onboarding__button--small"
      disabled={invitation.copying}
      onClick={onCopyLink}
    >
      {invitation.copied ? <BiCheck size={15} /> : <BiCopy size={15} />}
      {invitation.copied ? DETAIL.INVITATION.COPIED : DETAIL.INVITATION.COPY}
    </button>
  );

  const groups = SECTIONS.filter((key) => !isEmpty(todo[key])).map((key) => ({
    step: key,
    index: SECTIONS.indexOf(key),
    title: get(SECTION_TEXT, [key, "TITLE"]),
    items: todo[key],
    action: key === STEP_KEYS.ADMIN ? copyButton : null,
  }));

  return (
    <div className="onboarding">
      <header className="onboarding__header">
        <div className="onboarding__header-inner">
          <div className="onboarding__heading">
            <span className="onboarding__eyebrow">{DETAIL.EYEBROW}</span>
            <div className="onboarding__title-row">
              <h1 className="onboarding__title">
                {get(initialValues, "organization.name")}
              </h1>
              <span className="onboarding__chip">
                {get(GENERAL.CATEGORIES, type, type)}
              </span>
            </div>
          </div>
          <div className="onboarding__header-actions">
            <button
              type="button"
              className="onboarding__button onboarding__button--outline onboarding__button--icon"
              onClick={onCancel}
            >
              <BiChevronLeft size={16} />
              {DETAIL.HEADER.DISCARD}
            </button>
            <button
              type="button"
              className="onboarding__button onboarding__button--danger onboarding__button--icon"
              onClick={onDelete}
            >
              <BiTrash size={16} />
              {DETAIL.HEADER.DELETE}
            </button>
            <button
              type="button"
              className="onboarding__button onboarding__button--primary onboarding__button--icon"
              disabled={saving}
              onClick={save}
            >
              <BiCheck size={16} />
              {DETAIL.HEADER.SAVE}
            </button>
          </div>
        </div>
      </header>

      <div className="onboarding__body">
        <nav aria-label={DETAIL.SECTIONS_LABEL} className="onboarding__nav">
          <ol className="onboarding__steps">
            {SECTIONS.map((key) => {
              const current = key === section;
              const status = statusOf(key);
              return (
                <li key={key}>
                  <button
                    type="button"
                    aria-current={current ? "page" : undefined}
                    className={classNames(
                      "onboarding__step",
                      `onboarding__step--${status.tone}`,
                      { "onboarding__step--current": current },
                    )}
                    onClick={() => setSection(key)}
                  >
                    <span className="onboarding__step-mark">
                      {status.tone === STATUS_TONES.DONE ? (
                        <BiCheck size={16} aria-hidden="true" />
                      ) : null}
                      {status.tone === STATUS_TONES.LOCKED ? (
                        <BiLock size={14} aria-hidden="true" />
                      ) : null}
                      {ATTENTION_TONES.includes(status.tone) ? (
                        <span className="onboarding__step-dot" />
                      ) : null}
                    </span>
                    <span className="onboarding__step-text">
                      <span className="onboarding__step-title">
                        {get(SECTION_TEXT, [key, "TITLE"])}
                      </span>
                      <span
                        className={`onboarding__step-status onboarding__step-status--${status.tone}`}
                      >
                        {status.text}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <section
          aria-labelledby="organization-section-title"
          className="onboarding__main"
        >
          <div className="onboarding__main-head">
            <h2
              id="organization-section-title"
              className="onboarding__main-title"
            >
              {get(SECTION_TEXT, [section, "TITLE"])}
            </h2>
            <p className="onboarding__lead">
              {get(SECTION_TEXT, [section, "LEAD"])}
            </p>
          </div>
          <div className="onboarding__content">
            {section === STEP_KEYS.GENERAL ? (
              <section className="onboarding__section">
                <div className="onboarding__grid">
                  <TextField
                    name="organization.name"
                    label={GENERAL.NAME}
                    required
                  />
                  <div className="onboarding__field">
                    <span>{GENERAL.CATEGORY}</span>
                    <span className="onboarding__static">
                      {get(GENERAL.CATEGORIES, type, type)}
                      <BiLock size={16} aria-hidden="true" />
                    </span>
                    <span className="onboarding__help">
                      {DETAIL.CATEGORY_LOCKED}
                    </span>
                  </div>
                  <SelectField
                    name="organization.form"
                    label={GENERAL.FORM}
                    placeholder={GENERAL.FORM_PLACEHOLDER}
                    later={DETAIL.TO_COMPLETE}
                    options={Object.values(ORGANIZATION_FORMS).map((value) => ({
                      value,
                      label: get(formOptions, value, value),
                    }))}
                  />
                  <TextField
                    name="organization.siretNumber"
                    label={GENERAL.SIRET}
                    placeholder={GENERAL.SIRET_PLACEHOLDER}
                    inputMode="numeric"
                    later={DETAIL.TO_COMPLETE}
                  />
                  <TextField
                    name="telecoms.PHONE"
                    label={GENERAL.PHONE}
                    placeholder={GENERAL.PHONE_PLACEHOLDER}
                    type="tel"
                  />
                  <TextField
                    name="telecoms.EMAIL"
                    label={GENERAL.EMAIL}
                    placeholder={GENERAL.EMAIL_PLACEHOLDER}
                    type="email"
                  />
                  <TextField
                    name="telecoms.ADDRESS"
                    label={GENERAL.ADDRESS}
                    placeholder={GENERAL.ADDRESS_PLACEHOLDER}
                  />
                  <div className="onboarding__pair">
                    <TextField
                      name="telecoms.ZIP_CODE"
                      label={GENERAL.ZIP_CODE}
                      placeholder={GENERAL.ZIP_CODE_PLACEHOLDER}
                      inputMode="numeric"
                    />
                    <TextField
                      name="telecoms.CITY"
                      label={GENERAL.CITY}
                      placeholder={GENERAL.CITY_PLACEHOLDER}
                    />
                  </div>
                </div>
                <Logo labels={GENERAL} />
              </section>
            ) : null}
            {section === STEP_KEYS.ADMIN ? (
              <section className="onboarding__section onboarding__section--roomy">
                <Invitation
                  invitation={invitation}
                  email={get(initialValues, "user.email")}
                  name={adminName}
                  labels={DETAIL.INVITATION}
                  onCopyLink={onCopyLink}
                  onResend={onResend}
                />
                <div className="onboarding__fields">
                  <div className="onboarding__grid onboarding__grid--thirds">
                    <Civility labels={GENERAL} />
                    <TextField
                      name="user.lastname"
                      label={GENERAL.LASTNAME}
                      placeholder={GENERAL.LASTNAME_PLACEHOLDER}
                      required
                    />
                    <TextField
                      name="user.firstname"
                      label={GENERAL.FIRSTNAME}
                      placeholder={GENERAL.FIRSTNAME_PLACEHOLDER}
                      required
                    />
                  </div>
                  <div className="onboarding__grid">
                    {emailReadOnly ? (
                      <div className="onboarding__field">
                        <span>{GENERAL.USER_EMAIL}</span>
                        <span className="onboarding__static">
                          {get(values, "user.email")}
                          <BiLock size={16} aria-hidden="true" />
                        </span>
                        <span className="onboarding__help">
                          {DETAIL.EMAIL_LOCKED}
                        </span>
                      </div>
                    ) : (
                      <TextField
                        name="user.email"
                        label={GENERAL.USER_EMAIL}
                        placeholder={GENERAL.USER_EMAIL_PLACEHOLDER}
                        type="email"
                        hint={DETAIL.LOGIN_HINT}
                        required
                      />
                    )}
                    <TextField
                      name="user.phone"
                      label={GENERAL.USER_PHONE}
                      placeholder={GENERAL.USER_PHONE_PLACEHOLDER}
                      type="tel"
                      required
                    />
                  </div>
                </div>
              </section>
            ) : null}
            {section === STEP_KEYS.KEY ? (
              <EncryptionKey
                encryption={encryption}
                labels={{ ...ENCRYPTION, HANDOVER: DETAIL.HANDOVER }}
                busy={encryptionBusy}
                adminEmail={get(initialValues, "user.email")}
                onLink={onLinkKey}
                onPrepare={onPrepareKeyImport}
                onImport={onImportKey}
                onVerify={onVerifyKey}
                onSendHandover={onSendKeyHandover}
              />
            ) : null}
            {section === STEP_KEYS.INTEGRATIONS ? (
              <IntegrationsStep
                catalog={catalog}
                labels={ONBOARDING.INTEGRATIONS}
                keysLocked={keysLocked}
                lockedLabels={ENCRYPTION}
                onConfigureKey={() => setSection(STEP_KEYS.KEY)}
              />
            ) : null}
            {section === STEP_KEYS.API ? (
              <ApiAccess
                access={api.access}
                labels={{ ...DETAIL.API, HANDOVER: DETAIL.HANDOVER }}
                busy={api.busy}
                adminEmail={get(initialValues, "user.email")}
                onToggle={api.onToggle}
                onCreate={api.onCreate}
                onDelete={api.onDelete}
                onResend={api.onResend}
              />
            ) : null}
            {SETUP_SECTIONS.includes(section) ? (
              <SetupList
                items={setupItemsOf(section, type)}
                setup={setup}
                labels={DETAIL.SETUP}
                withIcon={section === STEP_KEYS.DOCUMENTS}
              />
            ) : null}
          </div>
        </section>

        <RemainingPanel
          groups={groups}
          labels={DETAIL.REMAINING}
          onGo={(index) => setSection(SECTIONS[index])}
        />
      </div>
    </div>
  );
};

Page.propTypes = {
  catalog: PropTypes.arrayOf(PropTypes.object).isRequired,
  setup: PropTypes.object.isRequired,
  invitation: PropTypes.object.isRequired,
  encryption: PropTypes.object.isRequired,
  encryptionBusy: PropTypes.bool.isRequired,
  api: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  emailReadOnly: PropTypes.bool,
  onCopyLink: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onLinkKey: PropTypes.func.isRequired,
  onPrepareKeyImport: PropTypes.func.isRequired,
  onImportKey: PropTypes.func.isRequired,
  onVerifyKey: PropTypes.func.isRequired,
  onSendKeyHandover: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

Page.defaultProps = {
  emailReadOnly: false,
};

const View = ({ initialValues, onSubmit, ...props }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
      onSubmit={onSubmit}
    >
      <Page {...props} />
    </Formik>
  );
};

View.propTypes = {
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default View;
